<template>
    <v-container class="setup-wizard" style="max-width: 560px;">
        <div v-if="checkingPayment" class="text-center py-12">
            <v-progress-circular indeterminate color="primary" class="mb-4"/>
            <p class="text-body-2 text-grey-darken-1">Checking your account...</p>
        </div>

        <div v-else-if="!showSuccess">
            <div class="text-center mb-2">
                <h2 class="text-h5 font-weight-bold">Welcome to SimpliGPS!</h2>
                <p class="text-body-2 text-grey-darken-1">Let's set up your fleet tracking in just a few steps</p>
            </div>

            <wizard-step-indicator :steps="stepLabels" :current-step="currentStep"/>

            <v-card class="pa-6" rounded="xl" elevation="2">
                <step-payment-method v-if="currentStep === 1" @payment-added="onPaymentAdded"/>

                <step-activate-device
                    v-else-if="currentStep === 2"
                    :key="activationKey"
                    @activated="onDeviceActivated"
                    @payment-required="onPaymentRequired"
                />

                <step-asset-type
                    v-else-if="currentStep === 3"
                    :key="assetStepsKey"
                    @selected="onAssetTypeSelected"
                />

                <step-asset-details
                    v-else-if="currentStep === 4"
                    :key="assetStepsKey"
                    :asset-type="selectedAssetType"
                    :device="activatedDevice"
                    @created="onAssetCreated"
                    @change-asset-type="onChangeAssetType"
                />

                <div v-if="currentStep > 1" class="mt-4">
                    <v-btn variant="text" size="small" @click="goBack">
                        <v-icon start size="18">mdi-arrow-left</v-icon>
                        Back
                    </v-btn>
                </div>
            </v-card>
        </div>

        <div v-else class="text-center py-12">
            <v-icon size="80" color="success" class="mb-4">mdi-check-circle</v-icon>
            <h2 class="text-h5 font-weight-bold mb-2">You're All Set!</h2>
            <p class="text-body-1 text-grey-darken-1 mb-2">
                Your device has been activated, your asset has been created, and everything is linked and ready to track.
            </p>
            <div v-if="activatedDevice?.slno || selectedAssetType?.name" class="d-flex justify-center ga-3 mb-4">
                <v-chip v-if="activatedDevice?.slno" size="small" variant="tonal" color="primary" prepend-icon="mdi-access-point">
                    {{ activatedDevice.slno }}
                </v-chip>
                <v-chip v-if="selectedAssetType?.name" size="small" variant="tonal" color="primary" prepend-icon="mdi-shape-outline">
                    {{ selectedAssetType.name }}
                </v-chip>
            </div>
            <div class="d-flex flex-column ga-3 mx-auto" style="max-width: 300px;">
                <v-btn color="primary" size="large" block @click="activateAnother">
                    <v-icon start>mdi-plus</v-icon>
                    Add Another Device
                </v-btn>
                <v-btn variant="outlined" color="primary" size="large" block @click="navigateTo('live-view')">
                    <v-icon start>mdi-map-marker</v-icon>
                    Start Tracking
                </v-btn>
            </div>
        </div>
    </v-container>
</template>

<script setup>
import {ref, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useNavigation} from "@/composables/globals";
import {useOnboardingStore} from "@/stores/onboardingStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import WizardStepIndicator from "@/components/onboarding/WizardStepIndicator.vue";
import StepActivateDevice from "@/components/onboarding/StepActivateDevice.vue";
import StepPaymentMethod from "@/components/onboarding/StepPaymentMethod.vue";
import StepAssetType from "@/components/onboarding/StepAssetType.vue";
import StepAssetDetails from "@/components/onboarding/StepAssetDetails.vue";

const {navigateTo} = useNavigation();
const onboardingStore = useOnboardingStore();
const snackbar = useSnackbarStore();

const stepLabels = ['Payment', 'Activate Device', 'Asset Type', 'Asset Setup'];
const currentStep = ref(onboardingStore.wizardStep || 1);
const activatedDevice = ref(onboardingStore.wizardDevice || null);
const selectedAssetType = ref(onboardingStore.wizardAssetType || null);
const showSuccess = ref(false);
const checkingPayment = ref(true);
const activationKey = ref(0);
const assetStepsKey = ref(0);

const onPaymentAdded = () => {
    // Payment saved - proceed to activate device step
    setStep(2);
};

const onDeviceActivated = (device) => {
    activatedDevice.value = device;
    onboardingStore.setWizardDevice(device);
    onboardingStore.markDeviceActivated();
    setStep(3);
};

const onPaymentRequired = () => {
    // Activation API returned 303 (no payment method) - go back to payment step
    snackbar.showSnackbar('A payment method is required before activating a device. Please add one below.', 'warning');
    setStep(1);
};

const onAssetTypeSelected = (type) => {
    selectedAssetType.value = type;
    onboardingStore.setWizardAssetType(type);
    setStep(4);
};

const onChangeAssetType = () => {
    setStep(3);
};

const onAssetCreated = () => {
    onboardingStore.markAssetCreated();
    onboardingStore.completeWizard();
    showSuccess.value = true;
};

const activateAnother = () => {
    // Reset for next device - payment is already saved, start at activate step
    activatedDevice.value = null;
    selectedAssetType.value = null;
    showSuccess.value = false;
    activationKey.value++; // Force re-create activate device step to clear form
    assetStepsKey.value++; // Force re-create asset steps to clear stale data
    onboardingStore.setWizardDevice(null);
    onboardingStore.setWizardAssetType(null);
    setStep(2);
};

const goBack = () => {
    if (currentStep.value > 1) {
        setStep(currentStep.value - 1);
    }
};

const setStep = (step) => {
    currentStep.value = step;
    onboardingStore.setWizardStep(step);
};

const checkPaymentMethods = async () => {
    try {
        const response = await send('get', 'payments/methods');
        const data = response.data?.data;
        const methods = Array.isArray(data) ? data : (data?.payment_methods || []);
        if (methods.length > 0) {
            // Already has a payment method - skip to activate device step
            if (currentStep.value < 2) {
                setStep(2);
            }
        }
    } catch (e) {
        // If we can't check, start at payment step (safe default)
    } finally {
        checkingPayment.value = false;
    }
};

onMounted(async () => {
    // Opened manually after setup was already completed (adding another device): don't
    // resume a stale mid-flow step from a previous session, start fresh.
    if (onboardingStore.hasCompletedSetup) {
        onboardingStore.setWizardDevice(null);
        onboardingStore.setWizardAssetType(null);
        activatedDevice.value = null;
        selectedAssetType.value = null;
        currentStep.value = 1;
        setStep(1);
    }

    // Guard: if restored to step 3+ without required data, reset to step 2
    if (currentStep.value >= 3 && !activatedDevice.value) {
        setStep(2);
    }
    if (currentStep.value >= 4 && !selectedAssetType.value) {
        setStep(3);
    }

    // If returning with an already-activated device, jump to step 3
    if (activatedDevice.value && currentStep.value < 3) {
        setStep(3);
        checkingPayment.value = false;
    } else if (currentStep.value >= 2) {
        // Returning to a later step - no need to re-check payment
        checkingPayment.value = false;
    } else {
        // Check if payment method already exists
        await checkPaymentMethods();
    }
});
</script>

<style scoped>
.setup-wizard {
    padding-top: 24px;
}
</style>
